import React, { Dispatch } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import Option from '@/components/unit-option';

import {
    ANIMATION_DELAY,
    ANIMATION_SPEED,
    optionsCardVariants,
} from '@/features/rapidstor/animations';
import {
    DescriptorOptions,
    unitOptionsTransformer,
    UpgradeDescriptor,
} from '@/features/rapidstor/UnitOptions';

import baseClasses from '../styles.module.scss';

interface Props {
    descriptor: Descriptor;
    descriptorOptions: UpgradeDescriptor[];
    unitTypeOptions: UnitType[];
    desktop: boolean;
    initialRate: number;

    selectedID: string;
    setSelectedID: Dispatch<React.SetStateAction<string>>;

    loading: boolean;
    setLoading: Dispatch<React.SetStateAction<boolean>>;
}

export default function OptionsCard({
    descriptor,
    descriptorOptions,
    unitTypeOptions,
    desktop,
    initialRate,
    selectedID,
    setSelectedID,
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();

    const useDescriptorOptions = DescriptorOptions(descriptor, descriptorOptions, location);

    return (
        <motion.div
            layout={true}
            variants={optionsCardVariants}
            initial='start'
            animate='show'
            transition={{
                duration: ANIMATION_SPEED,
                delay: ANIMATION_DELAY * 2,
            }}
            style={{
                maxWidth: '420px',
                width: '100%',
            }}
        >
            <div
                className={`${baseClasses.container} ${desktop ? baseClasses.desktop : baseClasses.mobile
                    }`}
            >
                <div className={`${baseClasses.customise}`}>
                    <span className={`${baseClasses.alternateOptions}`}>{t('unit_card:alternative_unit_options')}</span>
                    <p className={`${baseClasses.alternateDescription}`}>{descriptor.description}</p>
                </div>

                {useDescriptorOptions.isDescriptorUpsell() ? (
                    <>
                        {descriptorOptions.map((option) => unitOptionsTransformer(option)[0] ? (
                            <Option
                                key={`${option._id}_upsell_options_idx`}
                                location={location}
                                option={unitOptionsTransformer(option)[0]}
                                desktopOption={desktop}
                                initialRate={initialRate}
                                selected={selectedID === unitOptionsTransformer(option)[0]._id}
                                descriptor={option}
                                setSelectedID={setSelectedID}
                                theme={`base`}
                            />
                        ) : <em key={`${option._id}_upsell_options_idx`}>{t('unit_card:no_units_available')}</em>)}
                    </>
                ) : (
                    <>
                        {unitTypeOptions.map((option) => (
                            <Option
                                key={`${option._id}_upsell_options_idx`}
                                location={location}
                                option={option}
                                selected={selectedID === option._id}
                                desktopOption={desktop}
                                setSelectedID={setSelectedID}
                                initialRate={initialRate}
                                theme={`base`}
                            />
                        ))}
                    </>
                )}
            </div>
        </motion.div>
    );
}
